import { create } from 'zustand';
import type { CaseSite, PublicSite } from '../api/types';
import { useI18n } from '../i18n';

/**
 * Country site of the case being viewed (from the `site` block of GET /cabinet/cases/{id}).
 * A Moldovan case shows Moldovan contacts in the footer and opens in Romanian (unless a language was chosen);
 * the language menu lists the case site's languages (ro, ru) first. Cleared when the page is left.
 */
interface CaseSiteState {
  /** Who published the site: the case id, or 'list' for the cases list (all cases share one site). */
  owner: string | null;
  site: CaseSite | null;
  publish: (owner: string, site: CaseSite | null | undefined) => void;
  clear: (owner: string) => void;
}

export const useCaseSite = create<CaseSiteState>((set, get) => ({
  owner: null,
  site: null,
  publish(owner, site) {
    set({ owner, site: site ?? null });
    useI18n.getState().setCaseSiteLocale(site?.default_locale ?? null);
  },
  clear(owner) {
    if (get().owner !== owner) return; // another page already published its own site
    set({ owner: null, site: null });
    useI18n.getState().setCaseSiteLocale(null);
  },
}));

export interface SiteContacts {
  phone: string;
  duty: boolean;
  email: string;
  brand: string;
}

/** Footer contacts: the case site wins over the site of this cabinet (GET /public/site). Empty values are hidden. */
export function footerContacts(caseSite: CaseSite | null | undefined, site: PublicSite | null | undefined): SiteContacts {
  const pick = (s: { phone?: string | null; duty_phone?: string | null; email?: string | null } | null | undefined) => ({
    phone: s?.duty_phone || s?.phone || '',
    duty: !!s?.duty_phone,
    email: s?.email || '',
  });
  const fromCase = pick(caseSite);
  if (caseSite && (fromCase.phone || fromCase.email)) return { ...fromCase, brand: caseSite.brand || '' };
  const settings = (site?.settings ?? {}) as Record<string, unknown>;
  const brand = typeof settings.brand === 'string' ? settings.brand : typeof settings.name === 'string' ? settings.name : '';
  return { ...pick(site), brand };
}

/** The one site all the person's cases share (to keep the list in the same language), else null. */
export function commonSite(sites: (CaseSite | null | undefined)[]): CaseSite | null {
  const real = sites.filter((s): s is CaseSite => !!s);
  if (!real.length || real.length !== sites.length) return null;
  const first = real[0]!;
  return real.every((s) => s.country === first.country && s.default_locale === first.default_locale) ? first : null;
}
